import { Ship, Zap, Anchor, Plane, ShieldCheck } from "lucide-react";

export type BriefingId = "jet-selle" | "flyboard" | "engins-tractes" | "efoil" | "armement-moniteur";

export type BriefingSection = { title: string; items: string[] };

export type Briefing = {
  id: BriefingId;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  intro: string;
  duration: string;
  sections: BriefingSection[];
  safety: string[];
};

export const buildBriefingSpeech = (b: Pick<Briefing, "label" | "intro" | "sections" | "safety">) =>
  [
    `Briefing ${b.label}.`,
    b.intro,
    ...b.sections.map((s) => `${s.title}. ${s.items.join(" ")}`),
    `Rappels sécurité. ${b.safety.join(" ")}`,
  ].join(" ");

export const BRIEFINGS: Briefing[] = [
  {
    id: "jet-selle",
    label: "Jet à selle",
    icon: Ship,
    color: "text-sky-400",
    duration: "≈ 8 min 30 (max)",
    intro:
      "Briefing professionnel BPJEPS Motonautisme — Jet à selle (runabout). Structure officielle suivant le déroulé moniteur de l'Espace nautique (présentation → mise à l'eau → débriefing). Durée cible : 8 min 30 max.",
    sections: [
      { title: "Présentation", items: [
        "Retirer ses lunettes de soleil.",
        "Bonjour et bienvenue à l'Espace nautique. Je m'appelle Tommy, je serai votre moniteur pour l'activité jet-ski.",
        "Avez-vous tous rempli les contrats à l'accueil ? Si oui, je les récupère.",
        "Tout le monde a au moins 16 ans ? Décharge parentale signée pour les mineurs ?",
      ]},
      { title: "Gilet, second & équipement", items: [
        "Vous avez tous une combinaison et un gilet de sauvetage à votre taille ?",
        "Vérifier que les gilets sont bien fermés et ajustés — pilote ET passager (le « second »).",
        "Le second (passager) porte le même équipement que le pilote : combinaison + gilet.",
        "Quelqu'un a-t-il déjà fait du jet-ski ? Si oui, quand et où (mer ou lac) ?",
      ]},
      { title: "Contre-indications & appréhensions", items: [
        "Y a-t-il des personnes ayant une contre-indication médicale à la pratique ?",
        "Blessures, opérations récentes, problèmes cardiaques.",
        "Femmes enceintes : activité déconseillée.",
        "Appréhensions ? Peur de l'eau / ne sait pas nager : la combinaison et le gilet vous font flotter.",
        "Peur de la vitesse : chacun va à son rythme.",
      ]},
      { title: "Présentation du jet — avant / arrière / montée", items: [
        "Approchez-vous de la machine, je vais vous expliquer son fonctionnement.",
        "Voici l'avant du jet : il y a un coffre avec le matériel de sécurité.",
        "Voici l'arrière du jet.",
        "On monte TOUJOURS par l'arrière : c'est la partie la plus basse et la plus stable.",
        "On pose d'abord le genou en s'aidant de la poignée arrière. Jamais par les côtés : risque de blessure ou de retourner la machine.",
      ]},
      { title: "Machine retournée", items: [
        "Si vous retournez la machine, une flèche sur la coque indique le sens dans lequel il faut la remettre.",
        "Attendre que le liquide redescende avant de redémarrer.",
      ]},
      { title: "Position de pilotage", items: [
        "S'installer sur le siège pilote.",
        "Position : talons, hanches et épaules alignés.",
        "Jambes fléchies pour protéger le dos.",
      ]},
      { title: "Éviter les chocs au guidon", items: [
        "Bras légèrement pliés, coudes relevés pour absorber les chocs.",
        "Mains en pronation. Dynamique sur la machine. Regard au loin.",
        "Le passager met ses bras autour du pilote et décale sa tête pour éviter les chocs tête contre tête.",
        "4 dangers principaux : hydrojet, choc au guidon, éjection, impact / collision.",
      ]},
      { title: "Coupe-circuit", items: [
        "Prendre le coupe-circuit vert et le montrer.",
        "Il vous relie à la machine et permet de la démarrer / l'éteindre.",
        "On positionne l'extrémité dans une boucle du gilet de sauvetage.",
        "Appuyer FORT une fois sur le bouton rouge, insérer l'extrémité sur la petite boule sous le guidon : vous entendrez deux « bip ».",
      ]},
      { title: "Démarrage, arrêt & chute", items: [
        "Démarrer : appuyer FORT sur le bouton rouge à gauche du guidon jusqu'à ce qu'il s'allume — un « bip ».",
        "Éteindre : appuyer FORT sur le même bouton rouge, OU tirer sur le coupe-circuit.",
        "En cas de chute, le coupe-circuit part avec vous : la machine s'éteint automatiquement.",
        "Vous remontez en sécurité par l'arrière.",
      ]},
      { title: "Pas de gaz, pas de direction (hydrojet)", items: [
        "Le jet aspire l'eau par-dessous et la propulse par l'arrière en hydrojet (100 ch — très puissant).",
        "Gâchette d'accélération à droite du guidon.",
        "Pour se diriger : guidon + regard + accélérateur. Direction = accélération constante.",
        "Si vous lâchez les gaz et tournez le guidon, la machine ira tout droit.",
        "Si quelqu'un tombe à l'eau, ÉTEINDRE impérativement la machine avant de s'approcher.",
      ]},
      { title: "Pas de freins", items: [
        "Point très important : sur un jet, il N'Y A PAS DE FREINS.",
      ]},
      { title: "Gâchette d'accélération", items: [
        "Gérer la gâchette uniquement avec l'index : meilleur contrôle.",
        "Au démarrage, la machine est au neutre.",
        "Marche avant : appuyer une fois sur la gâchette droite avec l'index.",
        "Retour au neutre : appuyer une fois sur la gâchette de gauche.",
        "Ne JAMAIS appuyer sur les 2 gâchettes en même temps : risque d'endommager la machine et de perdre la caution.",
      ]},
      { title: "Éjection", items: [
        "Attention dans les virages : si vous tournez trop vite et trop fort le guidon, vous risquez d'être ÉJECTÉ.",
      ]},
      { title: "Communication — signes (chorégraphie)", items: [
        "Sur l'eau vous ne m'entendrez pas, on communique avec des signes :",
        "Main en haut = revenir vers moi au ralenti.",
        "Bras en croix = éteindre la machine (bouton rouge ou coupe-circuit).",
        "Main agitée vers le haut = accélérer. Vers le bas = ralentir.",
        "Main vers moi = se rapprocher. Main qui s'éloigne = s'éloigner.",
        "Doigt tournant vers le bas = tourner sur place en braquant le guidon.",
        "Doigt tournant vers le haut = allumer votre machine.",
        "Bras agité de haut en bas = file indienne.",
      ]},
      { title: "Le parcours — circuit 1", items: [
        "Se diriger vers le plan avec le groupe.",
        "Nous serons sur le circuit numéro 1, départ de la plage.",
        "Les 2 bouées jaunes délimitent la zone de RALENTI : pas d'accélération.",
        "Bouées blanches à votre DROITE, bouées rouges à votre GAUCHE.",
        "On ne dépasse JAMAIS le jet qui est devant vous.",
      ]},
      { title: "Distance de sécurité", items: [
        "Minimum 50 mètres entre chaque machine (≈ 2 bouées d'écart).",
        "Risque principal = collision.",
        "Avez-vous des questions ?",
      ]},
      { title: "Échauffement", items: [
        "Avant de débuter, échauffements ostéo-articulaires et cardiovasculaires (obligation).",
        "Cervicales, épaules, coudes, poignets, bassin, genoux, chevilles.",
        "Quelques squats / flexions pour activer le bas du corps.",
      ]},
      { title: "Point de départ — mise à l'eau", items: [
        "Allez vous munir du coupe-circuit vert et attachez-le sur votre gilet.",
        "Dirigez les machines vers le plan d'eau et avancez jusqu'à mi-cuisses dans l'eau.",
      ]},
      { title: "Position de départ", items: [
        "Conducteur en selle, coupe-circuit attaché au gilet, mains sur le guidon.",
        "Le passager (second) reste DANS L'EAU, à côté du jet, tenant la machine.",
        "Maintenir le jet à environ 1 mètre de profondeur pour qu'il reste stable en position de départ.",
        "Attendre mon signal avant tout démarrage.",
      ]},
      { title: "Départ un par un — file indienne", items: [
        "Je vous appellerai UN PAR UN pour me suivre.",
        "Vous vous suivrez ensuite en FILE INDIENNE.",
      ]},
      { title: "Premier tour de reconnaissance", items: [
        "Premier tour à rythme LENT : repérage du circuit, des bouées et de la distance de sécurité.",
        "Deuxième tour avec plus d'allure, puis évolution en autonomie.",
        "Restez derrière moi, respectez l'écart de 50 m.",
      ]},
      { title: "Débriefing final", items: [
        "Est-ce que tout le monde va bien ? Pas de blessé ?",
      ]},
      { title: "Étirements & hydratation", items: [
        "Nous allons nous étirer.",
        "N'oubliez pas de bien vous hydrater pour éviter les courbatures.",
        "Poubelles à disposition pour laisser le site propre.",
        "N'hésitez pas à laisser un commentaire sur nos réseaux si vous avez apprécié l'activité.",
        "Je vous souhaite une bonne journée et j'espère vous revoir très vite.",
      ]},
    ],
    safety: [
      "Bande des 300 m : 5 nœuds maximum, accès perpendiculaire au rivage.",
      "Navigation interdite la nuit (du coucher au lever du soleil).",
      "Âge minimum pilote : 16 ans avec permis côtier en cours de validité.",
      "Coupe-circuit attaché en permanence au gilet du pilote.",
      "Distance de sécurité : 50 m minimum entre machines (≈ 2 bouées).",
    ],
  },
  {
    id: "flyboard",
    label: "Flyboard",
    icon: Zap,
    color: "text-fuchsia-400",
    duration: "≈ 8 min 30 (max)",
    intro:
      "Briefing professionnel BPJEPS Motonautisme — Flyboard. Planche reliée à la turbine du jet par un tuyau haute pression. Déroulé moniteur (présentation → vol → débriefing). Durée cible : 8 min 30 max.",
    sections: [
      { title: "Présentation", items: [
        "Retirer ses lunettes de soleil.",
        "Bonjour et bienvenue à l'Espace Quilly. Je m'appelle Tommy, je vais être votre moniteur pour cette activité.",
        "Avez-vous rempli le contrat à l'accueil ? Si vous l'avez lu et signé, je vais le récupérer.",
        "Vous avez au moins 16 ans ? (Pour les mineurs, les parents doivent être présents.)",
      ]},
      { title: "Contre-indications", items: [
        "Est-ce que vous avez une contre-indication à la pratique de cette activité ?",
        "Blessures, opération, problèmes cardiaques…",
        "Des femmes enceintes ?",
      ]},
      { title: "Appréhensions", items: [
        "Est-ce que vous avez une appréhension de l'activité ?",
        "Ne pas savoir nager ou peur de l'eau : la combinaison et le gilet vous permettent de flotter.",
        "Peur de la hauteur / avoir le vertige : nous irons à une faible hauteur et j'adapterai au besoin.",
      ]},
      { title: "Équipement — combinaison, gilet, casque", items: [
        "Combinaison intégrale obligatoire — shorty interdit (risque hydrojet).",
        "Avez-vous une combinaison, un gilet de sauvetage et un casque à votre taille ?",
        "Port du casque dans le but d'éviter les chocs avec le jet.",
        "Pensez à serrer correctement votre casque pour des raisons de sécurité et de confort.",
        "Pensez à retirer vos bijoux et lunettes afin d'éviter de vous blesser ou de les perdre.",
      ]},
      { title: "Approchons-nous du Flyboard", items: [
        "Sur le Jet, il y aura un drapeau bleu et blanc : c'est le pavillon Alpha, qui a pour but de signaler notre activité.",
        "Il y aura également une personne qui m'assistera pour vous surveiller (sur le bateau ou la berge).",
        "Est-ce que quelqu'un a déjà fait du Flyboard ?",
      ]},
      { title: "Fonctionnement — je vais vous expliquer", items: [
        "Le Jet fonctionne avec une grille d'aspiration qui aspire de l'eau par le dessous.",
        "Cette eau est rejetée sous haute pression par l'arrière du Jet.",
        "L'eau continuera son chemin dans le tuyau qui est connecté au Jet.",
        "Au bout de ce tuyau se trouve la planche du Flyboard.",
        "L'eau sortira sous haute pression via les 2 extrémités du Flyboard.",
      ]},
      { title: "Qu'est-ce que cela signifie ?", items: [
        "Sur mon Jet, je dispose de la propulsion.",
        "Vous, sur le Flyboard, vous avez la direction.",
      ]},
      { title: "Déroulé de la séance — bonnes positions", items: [
        "On démarre sur le dos. Je vous indiquerai la direction à suivre. Dans un premier temps, tendez les jambes.",
        "Ensuite, pliez la jambe droite pour voir quand quelle direction vous allez. Puis, de nouveau jambes tendues, pliez la jambe gauche et observez la direction dans laquelle vous allez.",
        "2ᵉ étape (à montrer sur la berge) : balayez la planche avec vos chevilles pour voir la réaction de la planche. Une fois en l'air, ce mouvement vous permettra de garder votre équilibre.",
        "Pour aller en avant : pointe des pieds vers le BAS = on DESCEND et on avance, regard au loin.",
        "Pour se mettre sur le ventre : basculer les bras et les épaules dans un sens, puis le bassin et les jambes. C'est dans cette position que nous partirons.",
        "Pour décoller : position debout dans l'eau, jambes TENDUES, corps ALIGNÉ, pieds À PLAT, envoyez la planche au fond.",
        "Je mets les gaz progressivement. Vous montez en gardant les jambes tendues et le regard loin devant. Jamais vers vos pieds.",
      ]},
      { title: "Zone d'évolution & parcours", items: [
        "Je vais maintenant vous montrer la zone d'évolution avec les points de repère.",
        "Aujourd'hui, en fonction de votre progression, je vous ferai faire des tours autour de mon jet.",
        "Ensuite, on fera un parcours en serpent.",
        "Je vous donnerai toutes les informations pendant l'activité pour que vous puissiez évoluer au fur et à mesure.",
      ]},
      { title: "Échauffement", items: [
        "Avant de débuter l'activité, nous allons faire des échauffements ostéo-articulaires et cardiovasculaires.",
        "Cervicales, épaules, coudes, poignets, bassin, genoux, chevilles.",
      ]},
      { title: "On passe à l'équipement", items: [
        "On va passer à l'équipement.",
        "Aider le pratiquant à enfiler les chaussures et faire les lacets ou les scratchs.",
        "Attendre ma confirmation avant que j'envoie la pression.",
      ]},
      { title: "Débriefing", items: [
        "Est-ce que tout va bien ? Pas de blessure ?",
      ]},
      { title: "Étirements & hydratation", items: [
        "Nous allons maintenant nous étirer.",
        "N'oubliez pas de bien vous hydrater pour éviter les courbatures.",
        "Vous disposez de poubelles pour y jeter vos déchets afin de laisser le site propre.",
        "N'hésitez pas à nous mettre un commentaire sur les réseaux si vous avez apprécié l'activité.",
        "Je vous souhaite une bonne journée et j'espère vous revoir très vite.",
      ]},
    ],
    safety: [
      "Casque, combinaison et gilet de sauvetage obligatoires.",
      "Règle des 4/4/4 : 4 m de hauteur max, 4 m de profondeur min, 4 m d'écart minimum avec le jet.",
      "Interdiction formelle de plonger tête la première.",
      "Coupe-circuit moniteur opérationnel et système de release vérifié avant chaque session.",
      "Plan d'eau dégagé, profondeur contrôlée, zone balisée et exempte d'autres usagers.",
    ],
  },
  {
    id: "engins-tractes",
    label: "Engins tractés",
    icon: Anchor,
    color: "text-orange-400",
    duration: "≈ 8 min 30 (max)",
    intro:
      "Briefing professionnel BPJEPS Motonautisme — Engins tractés (bouées type Donuts monoplace, ski nautique, wakeboard). Déroulé moniteur (présentation → tractage → débriefing). Durée cible : 8 min 30 max.",
    sections: [
      { title: "Présentation", items: [
        "Retirer ses lunettes de soleil.",
        "Bonjour et bienvenue à l'Espace nautique. Je m'appelle Tommy, je serai votre moniteur pour l'activité engins tractés.",
        "Avez-vous tous rempli les contrats à l'accueil ? Si oui, je les récupère.",
        "Tout le monde a au moins 8 ans ? (âge minimum réglementaire pour la bouée tractée — Division 240). Décharge parentale signée pour les mineurs ?",
      ]},
      { title: "Gilet & équipement", items: [
        "Combinaison et gilet de sauvetage à votre taille, bien fermés.",
        "Retirer bijoux, montres, lunettes non attachées (blessures et perte).",
        "Casque recommandé pour les engins type bouée / airchair.",
        "Quelqu'un a-t-il déjà fait de la bouée tractée, du ski nautique ou du wakeboard ?",
      ]},
      { title: "Contre-indications & appréhensions", items: [
        "Contre-indications médicales : blessures, opérations récentes, problèmes cardiaques, dos / cervicales.",
        "Femmes enceintes : activité déconseillée.",
        "Peur de l'eau / ne sait pas nager : la combinaison et le gilet vous font flotter.",
        "Peur de la vitesse : on adapte le rythme à chacun.",
      ]},
      { title: "L'engin — présentation", items: [
        "Approchez-vous, je vais vous expliquer le fonctionnement, les règles de sécurité et la communication.",
        "L'engin est tracté par le bateau via une corde. Le bateau est piloté par moi, une vigie surveille en permanence.",
        "Une flamme ORANGE de 2 mètres est hissée à l'arrière du bateau pour signaler l'activité de tractage.",
        "Les autres bateaux doivent s'écarter d'au moins 100 mètres de l'engin tracté.",
        "Profondeur minimale : au moins 2 mètres sous l'engin tracté (exigence BPJEPS).",
      ]},
      { title: "Position sur la bouée (Donuts monoplace — exemple)", items: [
        "Assis dans la bouée, face au bateau, SANS s'enfoncer (évite les chocs dans le bas du dos).",
        "Poignées ORANGE = tenue principale. Mains en pronation sur les poignées noires.",
        "Ne JAMAIS passer les mains à l'intérieur des poignées (risque grave en cas de chute).",
        "Corde rouge entre les jambes, en direction du bateau : ne jamais y toucher pendant le tractage.",
        "Jambes écartées, genoux ouverts : stabilité et corde libre.",
        "Gainage léger, buste tonique. À l'arrêt et au démarrage, légèrement en arrière pour ne pas faire couler la bouée.",
        "Pieds et mains HORS de l'eau pour éviter les blessures liées à la vitesse.",
        "Variante tandem / biplace : briefing chahut différent — pas de changement de place, pas de chahut entre les deux bouées.",
      ]},
      { title: "Règles de comportement", items: [
        "Ne pas changer de position pendant le tractage.",
        "Ne pas se mettre debout.",
        "Ne pas se jeter volontairement à l'eau.",
        "Ne pas chahuter avec l'autre bouée (si tandem / biplace) — briefing chahut différent.",
      ]},
      { title: "Chute & remontée", items: [
        "En cas de chute : se laisser aller, NE JAMAIS s'opposer.",
        "Une fois dans l'eau : main levée hors de l'eau pour signaler votre position au bateau.",
        "Pour remonter : saisir les 2 petites poignées à l'avant de la bouée, passer un coude puis l'autre, puis un genou.",
        "Si vous n'y arrivez pas : tenez-vous à la bouée, je vous ramène au bateau en tirant sur la corde.",
      ]},
      { title: "Communication — signes", items: [
        "Sur l'eau vous ne m'entendrez pas, on communique avec des signes :",
        "Main levée hors de l'eau après une chute = signaler sa position.",
        "Signe « OK » avec les doigts = tout va bien (à confirmer quand demandé).",
        "Main sur la tête = problème, retour au bateau.",
        "Pouce vers le haut = accélérer. Pouce vers le bas = ralentir.",
      ]},
      { title: "Échauffement", items: [
        "Avant de débuter, échauffements ostéo-articulaires et cardiovasculaires (obligation).",
        "Cervicales, épaules, coudes, poignets, bassin, genoux, chevilles.",
        "Renforcer le gainage avec quelques exercices simples (sangle abdominale).",
      ]},
      { title: "Mise à l'eau & départ", items: [
        "Je mets la bouée à l'eau, vous montez dessus comme expliqué.",
        "Vous me confirmez « OK » quand vous êtes prêt(e).",
        "Premier tour à rythme lent, puis on adapte la vitesse à vos sensations.",
      ]},
      { title: "Débriefing final", items: [
        "Est-ce que tout le monde va bien ? Pas de blessé ?",
      ]},
      { title: "Étirements & hydratation", items: [
        "Nous allons nous étirer (épaules, dos, bras).",
        "N'oubliez pas de bien vous hydrater pour éviter les courbatures.",
        "Poubelles à disposition pour laisser le site propre.",
        "N'hésitez pas à laisser un commentaire sur nos réseaux si vous avez apprécié l'activité.",
        "Je vous souhaite une bonne journée et j'espère vous revoir très vite.",
      ]},
      { title: "Next Step Captain ROCA", items: [
        "Récapitulez les signes et la position correcte avant de monter sur la bouée.",
        "Vérifiez que vous avez bien retenu la règle des 100 mètres et la flamme orange.",
        "Prochaine étape : consultez le briefing Jet à selle ou E-Foil pour compléter votre formation BPJEPS.",
      ]},
    ],
    safety: [
      "Flamme ORANGE de 2 mètres obligatoire pour les activités de tractage (sauf plan d'eau privé sans autre navigant).",
      "Les autres bateaux doivent s'écarter d'au moins 100 mètres de l'engin tracté.",
      "Vigie de plus de 16 ans à bord pour surveiller et actionner la release en cas de danger.",
      "Couper le moteur AVANT toute récupération d'un pratiquant à l'eau. Jamais d'approche moteur engagé.",
      "Maintenir un contact visuel permanent avec le pratiquant.",
      "Ne jamais passer les mains à l'intérieur des poignées de bouée.",
    ],
  },
  {
    id: "efoil",
    label: "E-Foil",
    icon: Plane,
    color: "text-emerald-400",
    duration: "≈ 8 min 30 (max)",
    intro:
      "Briefing professionnel E-Foil — planche Lift Foils à propulsion électrique. Déroulé moniteur (présentation → vol → débriefing). Durée cible : 8 min 30 max.",
    sections: [
      { title: "Présentation", items: [
        "Retirer ses lunettes de soleil.",
        "Bonjour et bienvenue à l'Espace nautique. Je m'appelle Tommy, je serai votre moniteur pour l'activité E-Foil aujourd'hui.",
        "Avez-vous tous rempli les contrats à l'accueil ?",
        "Est-ce que tout le monde est majeur ? Sinon, quel âge avez-vous ? Décharge parentale signée pour les mineurs ?",
      ]},
      { title: "Gilet, casque & équipement", items: [
        "Gilet de sauvetage et CASQUE obligatoires, bien ajustés.",
        "La combinaison n'est pas obligatoire sur E-Foil.",
        "Quelqu'un a-t-il déjà fait du foil, du wakeboard, du surf ou de la planche à voile ?",
      ]},
      { title: "Contre-indications & appréhensions", items: [
        "Contre-indications médicales : blessures, opérations récentes, problèmes cardiaques, dos / cervicales.",
        "Femmes enceintes : activité déconseillée.",
        "Peur de l'eau / ne sait pas nager : la combinaison et le gilet vous font flotter.",
        "Peur de la hauteur : on commence toujours au ras de l'eau, on monte progressivement.",
      ]},
      { title: "Présentation du matériel — Lift Foils", items: [
        "Approchez-vous de la planche, je vais vous expliquer son fonctionnement, les règles de sécurité et la communication.",
        "Aujourd'hui nous allons évoluer sur une planche de la marque Lift Foils.",
        "La planche est équipée d'un mât avec un foil (aile) situé sous l'eau.",
        "Grâce à la vitesse, le foil soulève la planche et lui permet de « voler » au-dessus de l'eau.",
      ]},
      { title: "Télécommande — Power & gâchette", items: [
        "Le bouton Power se situe en haut de la télécommande : il sert à allumer et éteindre la planche.",
        "La vitesse est contrôlée par une gâchette : plus vous appuyez, plus la planche accélère.",
        "Le bouton sous l'écran règle le mode de puissance.",
        "Les boutons « + » et « – » augmentent ou diminuent la puissance dans le mode choisi.",
        "Je vous indiquerai le mode à utiliser pendant la séance.",
      ]},
      { title: "Sécurité — coupure automatique", items: [
        "La télécommande est flottante.",
        "Si la télécommande est immergée, le moteur se coupe immédiatement.",
        "Si la planche se retourne ou s'immerge fortement, le moteur se coupe également.",
        "INTERDICTION FORMELLE de plonger tête la première (foil dangereux sous l'eau).",
      ]},
      { title: "Mise à l'eau", items: [
        "Depuis la plage : portez la planche à L'ENVERS afin de protéger le foil situé sous la planche.",
        "Une fois dans l'eau, lorsque vous aurez de l'eau jusqu'à la taille, remettez la planche dans le bon sens.",
        "Depuis un ponton : le moniteur met directement la planche à l'eau, vous vous éloignez du bord en sécurité.",
      ]},
      { title: "Apprentissage — position allongée", items: [
        "Allongez-vous sur la planche.",
        "Appuyez légèrement sur la gâchette pour prendre un peu de vitesse.",
        "Restez détendu, regard loin devant.",
      ]},
      { title: "Apprentissage — passage à genoux", items: [
        "Mettez-vous progressivement à genoux sur la planche.",
        "Gardez les mains sur la planche pour vous stabiliser.",
        "Conservez une vitesse régulière avec la gâchette.",
      ]},
      { title: "Apprentissage — passage debout", items: [
        "Utilisez votre pied arrière pour vous relever progressivement.",
        "Une fois debout : accélérez légèrement.",
        "Appuyez doucement sur le pied avant pour faire déjauger la planche et prendre de la vitesse.",
        "Gardez TOUJOURS le pied arrière centré au milieu de la planche pour un maximum de stabilité.",
      ]},
      { title: "Décollage & vol", items: [
        "Une fois la bonne vitesse atteinte, relâchez légèrement l'appui sur le pied avant pour décoller.",
        "Regard loin devant (jamais vers les pieds).",
        "JAMAIS de grands mouvements de bras : cela vous déséquilibre.",
      ]},
      { title: "Pour redescendre", items: [
        "Appuyez de nouveau sur le pied avant pour reposer la planche.",
        "N'hésitez pas à reprendre contact avec l'eau pour retrouver votre équilibre.",
        "Mouvements doux et progressifs : pas de gestes brusques.",
      ]},
      { title: "Chute", items: [
        "Ne JAMAIS s'opposer à une chute : laissez-vous tomber sur le côté, loin du foil.",
        "Protégez le visage et la tête avec les bras.",
        "Le moteur se coupe automatiquement (télécommande immergée ou planche retournée).",
        "Une fois dans l'eau, signalez-vous, j'arrive avec le bateau.",
      ]},
      { title: "Communication pendant l'activité", items: [
        "Je serai sur l'eau avec le bateau pour m'approcher de vous à tout moment et vous donner un maximum de conseils.",
        "Nous serons reliés par radio afin que vous puissiez bien m'entendre pendant toute la séance.",
        "Signe « OK » avec les doigts = tout va bien.",
        "Main sur la tête = problème, retour bateau.",
        "Bras croisés = stop, on coupe.",
      ]},
      { title: "Échauffement", items: [
        "Avant de partir à l'eau, échauffement ostéo-articulaire et cardiovasculaire (obligation).",
        "Cervicales, épaules, coudes, poignets, bassin, genoux, chevilles.",
        "Quelques squats et flexions pour activer les jambes.",
      ]},
      { title: "Fin de séance", items: [
        "À la fin de l'activité, éteignez la télécommande grâce au bouton Power en haut.",
        "Le moniteur s'occupe ensuite du reste du matériel (planche, batterie, foil).",
      ]},
      { title: "Débriefing final", items: [
        "Est-ce que tout le monde va bien ? Pas de blessé ?",
      ]},
      { title: "Étirements & hydratation", items: [
        "Nous allons nous étirer (épaules, dos, mollets, ischio-jambiers).",
        "N'oubliez pas de bien vous hydrater pour éviter les courbatures.",
        "Poubelles à disposition pour laisser le site propre.",
        "N'hésitez pas à laisser un commentaire sur nos réseaux si vous avez apprécié l'activité.",
        "Je vous souhaite une bonne journée et j'espère vous revoir très vite.",
      ]},
    ],
    safety: [
      "Casque et gilet de sauvetage OBLIGATOIRES (combinaison non obligatoire).",
      "Interdiction formelle de plonger tête la première (foil tranchant sous l'eau).",
      "Télécommande flottante : moteur coupé automatiquement si immergée ou planche retournée.",
      "Distance de sécurité avec les autres usagers du plan d'eau : minimum 50 m.",
      "Porter la planche à l'envers pour protéger le foil lors des déplacements à terre.",
      "Vérification batterie, mât et foil avant chaque session.",
    ],
  },
  {
    id: "armement-moniteur",
    label: "Armement moniteur",
    icon: ShieldCheck,
    color: "text-emerald-400",
    duration: "Check-list pré-séance",
    intro:
      "Armement de sécurité OBLIGATOIRE pour le moniteur BPJEPS Motonautisme (VNM à selle / à bras) et pour le titulaire du Permis côtier encadrant une activité. Trois blocs : armement du VNM (Division 240), EPI du moniteur, matériel pro d'encadrement. À vérifier avant chaque mise à l'eau.",
    sections: [
      { title: "Cadre réglementaire", items: [
        "Division 240 — armement de sécurité plaisance applicable au VNM.",
        "Permis côtier obligatoire pour conduire un VNM en mer (≥ 16 ans).",
        "BPJEPS Motonautisme & Disciplines Associées (VNM à selle ou à bras) requis pour l'encadrement contre rémunération.",
        "Exigences variables selon distance d'abri (2 milles / 6 milles), plan d'eau (mer / eaux intérieures) et procédures de la base nautique.",
        "EPI conformes à la réglementation européenne (Règlement UE 2016/425).",
      ]},
      { title: "Armement VNM — jusqu'à 2 milles d'un abri", items: [
        "Gilet ou aide à la flottabilité 50 N minimum PAR PERSONNE à bord.",
        "Coupe-circuit relié EN PERMANENCE au pilote.",
        "Moyen lumineux individuel étanche (lampe flash).",
        "Dispositif de remorquage (bout de remorquage adapté).",
        "Extincteur si prévu par le constructeur du VNM.",
        "Combinaison néoprène adaptée (minimum 2 mm).",
        "Tous les équipements préconisés par le constructeur du VNM.",
      ]},
      { title: "Armement VNM — extension jusqu'à 6 milles d'un abri", items: [
        "Gilet 100 N (remplace le 50 N) par personne.",
        "3 feux rouges à main automatiques.",
        "Compas magnétique (ou électronique avec batterie de secours).",
        "Cartographie marine à jour de la zone (papier ou électronique).",
        "RIPAM — Règlement international pour prévenir les abordages en mer.",
        "Documentation du balisage maritime.",
        "Dispositif de réception des bulletins météo marine.",
      ]},
      { title: "EPI du moniteur — tenue obligatoire", items: [
        "Combinaison néoprène (intégrale 3/2 mm recommandée).",
        "Gilet impact ou aide à la flottabilité homologuée CE.",
        "Coupe-circuit attaché EN PERMANENCE au gilet.",
        "Chaussons ou bottillons nautiques.",
        "Lunettes flottantes ou masque de protection.",
        "Gants nautiques (souvent imposés par les bases).",
        "Casque pour activités spécifiques : freestyle, flyboard, initiations techniques.",
      ]},
      { title: "Matériel pro d'encadrement — base nautique sérieuse", items: [
        "VHF marine portable étanche (flottante).",
        "Téléphone étanche de secours (IP68).",
        "Couteau de sécurité marin.",
        "Trousse de premiers secours.",
        "Longe de remorquage renforcée (10–15 m).",
        "Sifflet de sécurité.",
        "GPS ou traceur.",
        "Lampe flash étanche.",
        "Sac étanche avec documents du bateau (acte de francisation, permis, assurance).",
      ]},
      { title: "Dossier administratif moniteur", items: [
        "Carte professionnelle BPJEPS à jour (renouvellement quinquennal).",
        "Permis côtier en cours de validité.",
        "Certificat médical de moins d'un an (non contre-indication à l'encadrement).",
        "Attestation PSC1 / PSE1 selon exigences employeur.",
        "Attestation d'assurance responsabilité civile professionnelle.",
        "Décharges signées et contrats clients vérifiés avant mise à l'eau.",
      ]},
      { title: "Kit \"Moniteur VNM professionnel\" idéal", items: [
        "Combinaison intégrale 3/2 mm.",
        "Gilet impact homologué CE.",
        "VHF portable flottante.",
        "Coupe-circuit spirale.",
        "Sifflet marin.",
        "Lampe flash étanche.",
        "Bout de remorquage 10–15 m.",
        "Couteau marin de sécurité.",
        "Pochette étanche documents.",
        "Téléphone étanche IP68.",
      ]},
      { title: "Check-list avant chaque mise à l'eau", items: [
        "Météo consultée et BMS vérifié (vent, mer, visibilité).",
        "Plan d'eau et zone d'évolution dégagés.",
        "Nombre de personnes encadrées conforme aux ratios BPJEPS.",
        "Coupe-circuit du moniteur attaché et testé.",
        "VHF allumée, canal 16 + canal base nautique.",
        "Bout de remorquage et matériel de premiers secours accessibles.",
        "Pavillon Alpha prêt si activité flyboard / plongée.",
        "Briefing client effectué et compris.",
      ]},
    ],
    safety: [
      "Le moniteur est responsable de la sécurité de tout le groupe : son armement doit être complet AVANT la mise à l'eau.",
      "Coupe-circuit attaché en permanence : c'est la première règle, jamais de dérogation.",
      "VHF canal 16 toujours opérationnelle pour appel de détresse.",
      "Au-delà de 2 milles d'un abri : compléter avec gilet 100 N, feux rouges, compas, cartes, RIPAM.",
      "Vérifier l'assurance RC pro et la carte professionnelle BPJEPS à jour avant toute prestation rémunérée.",
    ],
  },
];
